/**
 * ELITE BATCH 23 PART 7 — Workflows 101-118 (Support & Customer Success)
 * 18 support workflows: ticket triage, AI bots, SLA management, survey follow-ups.
 * Services: Zendesk, Intercom, Twilio, WhatsApp, Pinecone, OpenAI, HubSpot, Jira, Slack, Google Sheets.
 * Each workflow: 8 nodes, proper edges, descriptive names, y ~240 spacing.
 */

// ============ HELPER FUNCTIONS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// ============ WORKFLOWS 101-118 ============

export const ELITE_BATCH23_PART7: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ────────────────────────────────────────────────────────────────
  // 101. Ticket Received Confirmation | Support | Zendesk
  // ────────────────────────────────────────────────────────────────
  ticket_received_confirmation: {
    nodes: [
      trigger("Zendesk webhook: New ticket created", "webhook_app", {
        source: "zendesk",
        event: "ticket.created",
        howItStarts:
          "Fires when a new support ticket is created in Zendesk, sending ticket ID, subject, description, requester email, and priority to the workflow.",
        setup:
          "Connect your Zendesk instance and enable the ticket.created webhook trigger. Ensure all ticket fields are forwarded.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract ticket details and classify category",
        280,
        "You are a support ticket analyst. Extract the customer name, email, ticket subject, issue description, product mentioned, and classify into a category (billing, technical, account, general). Determine initial priority based on keywords (urgent, down, broken = high). Return customer_name, customer_email, subject, description, product, category, ai_priority.",
        "Analyze this new Zendesk support ticket and extract details: {webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "subject", as: "ticket_subject", type: "string" },
          { field: "category", as: "ticket_category", type: "string" },
          { field: "product", as: "product_mentioned", type: "string" },
          { field: "ai_priority", as: "ai_priority", type: "string" },
        ],
      ),
      n("3", "hitApi", "HubSpot: Lookup customer account and plan tier", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{customer_email}"}]}]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.plan_tier",
              customName: "plan_tier",
              type: "string",
            },
            {
              name: "results[0].properties.company",
              customName: "company_name",
              type: "string",
            },
            {
              name: "results[0].properties.hs_object_id",
              customName: "hubspot_contact_id",
              type: "string",
            },
          ],
        },
      }),
      n("4", "runCode", "Compute SLA deadline and final priority", 760, {
        runCodeDetails: {
          code: "const tierSLA = { enterprise: 2, professional: 4, starter: 8, free: 24 }; const sla_hours = tierSLA[plan_tier] || 24; const final_priority = (ai_priority === 'high' || plan_tier === 'enterprise') ? 'high' : ai_priority; return { sla_hours, final_priority, sla_deadline: new Date(Date.now() + sla_hours * 3600000).toISOString() };",
          variables: [
            { name: "sla_hours", customName: "sla_hours", type: "number" },
            {
              name: "final_priority",
              customName: "final_priority",
              type: "string",
            },
            {
              name: "sla_deadline",
              customName: "sla_deadline",
              type: "string",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Send ticket confirmation email with ETA to customer",
        1000,
        "{customer_email}",
        "We received your request: {ticket_subject} [#{ticket_id}]",
        "Hi {customer_name},\n\nThank you for reaching out. We've received your support request and a team member will respond within {sla_hours} hours.\n\nTicket: #{ticket_id}\nSubject: {ticket_subject}\nPriority: {final_priority}\n\nIn the meantime, you may find helpful resources at our help center: {help_center_url}\n\nBest,\nSupport Team",
      ),
      slackNode(
        "6",
        "Slack: Notify #support-queue with ticket details",
        1240,
        ":ticket: *New Ticket #{ticket_id}*\n*Customer:* {customer_name} ({company_name})\n*Plan:* {plan_tier} | *Priority:* {final_priority}\n*Category:* {ticket_category}\n*Subject:* {ticket_subject}\n*SLA Deadline:* {sla_deadline}",
      ),
      n(
        "7",
        "hitApi",
        "Zendesk: Update ticket with priority, tags, and SLA",
        1480,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{ticket_id}.json",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "ticket",
                value:
                  '{"priority":"{final_priority}","tags":["{ticket_category}","{plan_tier}"],"custom_fields":[{"id":360001,"value":"{sla_deadline}"}]}',
              },
            ],
            variables: [
              {
                name: "ticket.id",
                customName: "updated_ticket_id",
                type: "string",
              },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log ticket intake to Support Tracker",
        1720,
        "Ticket_Intake",
        '[["{ticket_id}","{customer_name}","{customer_email}","{company_name}","{plan_tier}","{ticket_category}","{final_priority}","{sla_hours}","{sla_deadline}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggers on new Zendesk tickets, uses AI to extract details and classify the category, looks up the customer in HubSpot to determine plan tier, computes SLA deadline and priority, sends a confirmation email with ETA, alerts the Slack support queue, updates Zendesk tags and priority, and logs to Google Sheets for tracking.",
  },

  // ────────────────────────────────────────────────────────────────
  // 102. AI First Response Suggestion | Support | Zendesk + Pinecone
  // ────────────────────────────────────────────────────────────────
  ai_first_response_suggestion: {
    nodes: [
      trigger(
        "Zendesk webhook: New ticket awaiting first response",
        "webhook_app",
        {
          source: "zendesk",
          event: "ticket.created",
          howItStarts:
            "Fires when a new Zendesk ticket is created and needs a first response, sending the ticket ID, subject, description, and requester details.",
          setup:
            "Connect Zendesk and enable ticket.created webhook. This workflow provides AI-drafted responses as internal notes for agent review.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Classify ticket intent and extract key terms",
        280,
        "You are a support intent classifier. Analyze the ticket and determine the primary intent (billing_question, bug_report, feature_request, how_to, account_access, integration_help, cancellation). Extract key terms for knowledge base search. Return intent, confidence, search_query, customer_sentiment.",
        "Classify this support ticket intent and extract search terms: Subject: {ticket_subject} Body: {ticket_description}",
        [
          { field: "intent", as: "ticket_intent", type: "string" },
          {
            field: "confidence",
            as: "classification_confidence",
            type: "number",
          },
          { field: "search_query", as: "kb_search_query", type: "string" },
          {
            field: "customer_sentiment",
            as: "customer_sentiment",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Pinecone: Search knowledge base for relevant articles",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{pinecone_index}-{pinecone_project}.svc.{pinecone_env}.pinecone.io/query",
            headers: [
              { key: "Api-Key", value: "{pinecone_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "vector", value: "{kb_search_embedding}" },
              { key: "topK", value: "5" },
              { key: "includeMetadata", value: "true" },
              { key: "namespace", value: "support_kb" },
            ],
            variables: [
              {
                name: "matches[0].metadata.title",
                customName: "article_1_title",
                type: "string",
              },
              {
                name: "matches[0].metadata.content",
                customName: "article_1_content",
                type: "string",
              },
              {
                name: "matches[0].metadata.url",
                customName: "article_1_url",
                type: "string",
              },
              {
                name: "matches[0].score",
                customName: "article_1_score",
                type: "number",
              },
              {
                name: "matches[1].metadata.title",
                customName: "article_2_title",
                type: "string",
              },
              {
                name: "matches[1].metadata.url",
                customName: "article_2_url",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "4",
        "OpenAI: Draft response with knowledge base citations",
        760,
        "You are a senior support agent. Using the provided knowledge base articles, draft a helpful, empathetic response to the customer ticket. Include specific steps and cite article URLs. Match the customer sentiment tone. Return draft_response, cited_articles, response_confidence.",
        "Draft a response for this ticket. Intent: {ticket_intent}. Ticket: {ticket_subject} - {ticket_description}. KB Article 1: {article_1_title} - {article_1_content}. KB Article 2: {article_2_title}. Article URLs: {article_1_url}, {article_2_url}",
        [
          { field: "draft_response", as: "draft_response", type: "string" },
          { field: "cited_articles", as: "cited_articles", type: "string" },
          {
            field: "response_confidence",
            as: "response_confidence",
            type: "number",
          },
        ],
      ),
      n("5", "condition", "Check: AI confidence > 80%?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{response_confidence}", operator: ">=", value: "80" },
          ],
        },
      }),
      n(
        "6",
        "hitApi",
        "Zendesk: Add AI draft as internal note for agent",
        1240,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{ticket_id}.json",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "ticket",
                value:
                  '{"comment":{"body":"**AI Suggested Response (confidence: {response_confidence}%):**\\n\\n{draft_response}\\n\\n**Sources:** {cited_articles}","public":false}}',
              },
            ],
            variables: [
              {
                name: "ticket.id",
                customName: "noted_ticket_id",
                type: "string",
              },
            ],
          },
        },
      ),
      slackNode(
        "7",
        "Slack: Suggest draft response to assigned agent",
        1480,
        ":robot_face: *AI Response Ready — Ticket #{ticket_id}*\n*Intent:* {ticket_intent} | *Confidence:* {response_confidence}%\n*Sentiment:* {customer_sentiment}\n\n*Draft:*\n{draft_response}\n\n*Sources:* {cited_articles}\n\n_Review and send from Zendesk, or edit as needed._",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log AI suggestion accuracy tracking",
        1720,
        "AI_Response_Log",
        '[["{ticket_id}","{ticket_intent}","{classification_confidence}","{response_confidence}","{article_1_score}","{customer_sentiment}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When a new Zendesk ticket arrives, AI classifies the intent and searches Pinecone knowledge base for relevant articles. A second AI call drafts a cited response. If confidence exceeds 80%, the draft is added as a Zendesk internal note; either way the agent gets a Slack notification with the suggested response and sources, and accuracy metrics are logged to Sheets.",
  },

  // ────────────────────────────────────────────────────────────────
  // 103. FAQ Chatbot with Agent Handoff | Support
  // ────────────────────────────────────────────────────────────────
  faq_chatbot_agent_handoff: {
    nodes: [
      trigger("Webhook: Incoming chat message received", "webhook_app", {
        source: "intercom",
        event: "conversation.user.created",
        howItStarts:
          "Fires when a customer sends a new chat message via your Intercom widget, delivering the message text, conversation ID, and user details.",
        setup:
          "Connect Intercom and enable conversation.user.created webhook. Configure to forward full message body and user metadata.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Classify chat intent and extract question",
        280,
        "You are a support chatbot classifier. Analyze the customer message and classify the intent (faq, billing, technical, sales, complaint, other). Extract the core question for KB search. Determine if the query is simple FAQ or needs a human agent. Return intent, core_question, needs_human, urgency_level.",
        "Classify this customer chat message: {message_text}",
        [
          { field: "intent", as: "chat_intent", type: "string" },
          { field: "core_question", as: "core_question", type: "string" },
          { field: "needs_human", as: "needs_human", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),
      n("3", "hitApi", "Pinecone: Search FAQ knowledge base", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{pinecone_index}-{pinecone_project}.svc.{pinecone_env}.pinecone.io/query",
          headers: [
            { key: "Api-Key", value: "{pinecone_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "vector", value: "{question_embedding}" },
            { key: "topK", value: "3" },
            { key: "includeMetadata", value: "true" },
            { key: "namespace", value: "faq" },
          ],
          variables: [
            {
              name: "matches[0].metadata.answer",
              customName: "faq_answer",
              type: "string",
            },
            {
              name: "matches[0].score",
              customName: "faq_match_score",
              type: "number",
            },
            {
              name: "matches[0].metadata.article_url",
              customName: "faq_article_url",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "4",
        "OpenAI: Generate conversational FAQ response",
        760,
        "You are a friendly support chatbot. Using the FAQ answer provided, generate a helpful, conversational reply to the customer. Keep it concise (2-3 sentences) and include the article link for more details. If the FAQ match is weak, suggest connecting with a human agent. Return bot_response, response_confidence.",
        "Customer question: {core_question}. FAQ answer: {faq_answer}. Match score: {faq_match_score}. Article URL: {faq_article_url}",
        [
          { field: "bot_response", as: "bot_response", type: "string" },
          {
            field: "response_confidence",
            as: "bot_confidence",
            type: "number",
          },
        ],
      ),
      n(
        "5",
        "condition",
        "Check: Bot confidence > 75% and no human needed?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { variable: "{bot_confidence}", operator: ">", value: "75" },
            ],
          },
        },
      ),
      n("6", "sendMessage", "Send FAQ bot reply to customer", 1240, {
        sendMessageDetails: {
          channel: "intercom",
          conversationId: "{conversation_id}",
          message: "{bot_response}",
        },
      }),
      slackNode(
        "7",
        "Slack: Send handoff package to human agent queue",
        1480,
        ":rotating_light: *Agent Handoff Needed — Conversation {conversation_id}*\n*Intent:* {chat_intent} | *Urgency:* {urgency_level}\n*Customer Question:* {core_question}\n*Bot Confidence:* {bot_confidence}%\n*Partial Answer:* {bot_response}\n\n_Please take over this conversation in Intercom._",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log chatbot interaction metrics",
        1720,
        "Chatbot_Log",
        '[["{conversation_id}","{chat_intent}","{core_question}","{faq_match_score}","{bot_confidence}","{needs_human}","{urgency_level}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
    summary:
      "Receives incoming Intercom chat messages, uses AI to classify intent, searches the Pinecone FAQ knowledge base, and generates a conversational response. If confidence exceeds 75%, sends the bot reply directly; otherwise escalates to a human agent via Slack with full context. All interactions are logged to Sheets for chatbot effectiveness analysis.",
  },

  // ────────────────────────────────────────────────────────────────
  // 104. After-Hours Support Bot | Support
  // ────────────────────────────────────────────────────────────────
  after_hours_support_bot: {
    nodes: [
      trigger("Webhook: Support message received", "webhook_app", {
        source: "intercom",
        event: "conversation.user.created",
        howItStarts:
          "Fires when a customer sends a message to support, delivering the message text, timestamp, user info, and conversation ID.",
        setup:
          "Connect Intercom webhook for new conversation messages. This workflow checks business hours before deciding to auto-respond or queue for next-day agent review.",
        inputFields: [],
      }),
      n(
        "2",
        "runCode",
        "Check if current time is outside business hours",
        280,
        {
          runCodeDetails: {
            code: "const now = new Date(); const hour = now.getUTCHours() - 5; const day = now.getUTCDay(); const is_after_hours = (hour < 9 || hour >= 18 || day === 0 || day === 6); const next_open = is_after_hours ? (day === 5 ? 'Monday 9:00 AM ET' : day === 6 ? 'Monday 9:00 AM ET' : 'tomorrow at 9:00 AM ET') : 'now'; return { is_after_hours, next_open };",
            variables: [
              {
                name: "is_after_hours",
                customName: "is_after_hours",
                type: "boolean",
              },
              {
                name: "next_open",
                customName: "next_open_time",
                type: "string",
              },
            ],
          },
        },
      ),
      n("3", "condition", "Check: Is it after hours?", 520, {
        conditionDetails: {
          conditions: [
            { variable: "{is_after_hours}", operator: "==", value: "true" },
          ],
        },
      }),
      aiNode(
        "4",
        "OpenAI: Classify urgency and search for quick answer",
        760,
        "You are an after-hours support triage bot. Classify the message urgency (critical, high, medium, low). Critical = service outage, security breach, payment processing down. Try to provide a helpful interim answer from common solutions. Return urgency, interim_answer, is_critical, category.",
        "Customer message received after hours: {message_text}",
        [
          { field: "urgency", as: "urgency_level", type: "string" },
          { field: "interim_answer", as: "interim_answer", type: "string" },
          { field: "is_critical", as: "is_critical", type: "string" },
          { field: "category", as: "issue_category", type: "string" },
        ],
      ),
      n("5", "hitApi", "Pinecone: Search KB for self-service solution", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{pinecone_index}-{pinecone_project}.svc.{pinecone_env}.pinecone.io/query",
          headers: [
            { key: "Api-Key", value: "{pinecone_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "vector", value: "{message_embedding}" },
            { key: "topK", value: "3" },
            { key: "includeMetadata", value: "true" },
            { key: "namespace", value: "support_kb" },
          ],
          variables: [
            {
              name: "matches[0].metadata.title",
              customName: "kb_article_title",
              type: "string",
            },
            {
              name: "matches[0].metadata.url",
              customName: "kb_article_url",
              type: "string",
            },
            {
              name: "matches[0].score",
              customName: "kb_relevance_score",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "6",
        "OpenAI: Compose after-hours auto-reply",
        1240,
        "You are a polite after-hours support bot. Compose a brief auto-reply acknowledging the customer message, providing the interim answer if relevant, linking to a KB article if the match score is high (>0.8), and setting expectations for when a human will follow up. Return auto_reply_message.",
        "Compose after-hours reply. Urgency: {urgency_level}. Interim answer: {interim_answer}. KB article: {kb_article_title} ({kb_article_url}, score: {kb_relevance_score}). Next open: {next_open_time}.",
        [
          {
            field: "auto_reply_message",
            as: "auto_reply_message",
            type: "string",
          },
        ],
      ),
      n("7", "sendMessage", "Send after-hours auto-reply with ETA", 1480, {
        sendMessageDetails: {
          channel: "intercom",
          conversationId: "{conversation_id}",
          message: "{auto_reply_message}",
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log after-hours interaction",
        1720,
        "After_Hours_Log",
        '[["{conversation_id}","{urgency_level}","{is_critical}","{issue_category}","{kb_relevance_score}","{next_open_time}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("3", "8", "output_2"),
    ],
    summary:
      "Receives support messages and checks whether the current time falls outside business hours. If after-hours, AI classifies urgency, searches the Pinecone knowledge base for a relevant self-service article, and composes an empathetic auto-reply with ETA for human follow-up. During business hours the interaction is simply logged. All after-hours contacts are tracked in Sheets.",
  },

  // ────────────────────────────────────────────────────────────────
  // 105. Multilingual Chat Support | Support
  // ────────────────────────────────────────────────────────────────
  multilingual_chat_support: {
    nodes: [
      trigger("Webhook: Chat message received in any language", "webhook_app", {
        source: "intercom",
        event: "conversation.user.created",
        howItStarts:
          "Fires when a customer sends a chat message in any language via Intercom, delivering the raw message text, conversation ID, and user metadata.",
        setup:
          "Connect Intercom webhook for new conversations. This workflow auto-detects the customer language, translates, responds from the KB, and translates back.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Detect language and translate to English",
        280,
        "You are a multilingual translator. Detect the language of the customer message and translate it to English. Preserve the original tone and any technical terms. Return detected_language, language_code, english_translation, is_english.",
        "Detect language and translate this customer message to English: {message_text}",
        [
          {
            field: "detected_language",
            as: "detected_language",
            type: "string",
          },
          { field: "language_code", as: "language_code", type: "string" },
          {
            field: "english_translation",
            as: "english_translation",
            type: "string",
          },
          { field: "is_english", as: "is_english", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "OpenAI: Classify intent from English translation",
        520,
        "You are a support intent classifier. Analyze the English-translated customer message and classify the intent. Extract key search terms for the knowledge base. Return intent, search_query, urgency.",
        "Classify support intent: {english_translation}",
        [
          { field: "intent", as: "ticket_intent", type: "string" },
          { field: "search_query", as: "kb_search_query", type: "string" },
          { field: "urgency", as: "urgency_level", type: "string" },
        ],
      ),
      n(
        "4",
        "hitApi",
        "Pinecone: Search knowledge base with English query",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{pinecone_index}-{pinecone_project}.svc.{pinecone_env}.pinecone.io/query",
            headers: [
              { key: "Api-Key", value: "{pinecone_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "vector", value: "{search_embedding}" },
              { key: "topK", value: "3" },
              { key: "includeMetadata", value: "true" },
              { key: "namespace", value: "support_kb" },
            ],
            variables: [
              {
                name: "matches[0].metadata.content",
                customName: "kb_answer_english",
                type: "string",
              },
              {
                name: "matches[0].metadata.url",
                customName: "kb_article_url",
                type: "string",
              },
              {
                name: "matches[0].score",
                customName: "kb_match_score",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "5",
        "OpenAI: Generate English response from KB content",
        1000,
        "You are a helpful support agent. Using the knowledge base answer, compose a concise and friendly response in English. Include the article URL for reference. Return english_response, response_confidence.",
        "Compose a support response. Customer intent: {ticket_intent}. Question: {english_translation}. KB answer: {kb_answer_english}. Article: {kb_article_url}. Match score: {kb_match_score}.",
        [
          { field: "english_response", as: "english_response", type: "string" },
          {
            field: "response_confidence",
            as: "response_confidence",
            type: "number",
          },
        ],
      ),
      aiNode(
        "6",
        "OpenAI: Translate response back to customer language",
        1240,
        "You are a multilingual translator. Translate the following English support response into {detected_language} ({language_code}). Keep technical terms clear and maintain a professional, friendly tone. If the language is English, return the response unchanged. Return translated_response.",
        "Translate this support response to {detected_language}: {english_response}",
        [
          {
            field: "translated_response",
            as: "translated_response",
            type: "string",
          },
        ],
      ),
      n("7", "sendMessage", "Send translated response to customer", 1480, {
        sendMessageDetails: {
          channel: "intercom",
          conversationId: "{conversation_id}",
          message: "{translated_response}",
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log multilingual interaction",
        1720,
        "Multilingual_Support",
        '[["{conversation_id}","{detected_language}","{ticket_intent}","{kb_match_score}","{response_confidence}","{urgency_level}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Handles incoming Intercom chat in any language by detecting the language with AI, translating to English, classifying intent, searching the Pinecone knowledge base, generating an English response, translating it back to the customer's language, and replying in-conversation. Logs language distribution and match quality to Sheets.",
  },

  // ────────────────────────────────────────────────────────────────
  // 106. SMS Customer Support Bot | Support | Twilio
  // ────────────────────────────────────────────────────────────────
  sms_customer_support_bot: {
    nodes: [
      trigger("Twilio webhook: Inbound SMS received", "webhook_app", {
        source: "twilio",
        event: "message.received",
        howItStarts:
          "Fires when a customer sends an SMS to your Twilio support number, delivering the message body, sender phone number, and message SID.",
        setup:
          "Configure your Twilio phone number to forward incoming SMS to this webhook URL. Ensure the number is published as your support SMS line.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract intent and compose concise SMS response",
        280,
        "You are an SMS support bot. Analyze the customer SMS and extract intent (order_status, billing, technical, general). Generate a concise response under 160 characters. Determine if a human agent is needed for complex issues. Return intent, sms_response, needs_agent, customer_name_guess.",
        "Customer SMS from {sender_phone}: {sms_body}",
        [
          { field: "intent", as: "sms_intent", type: "string" },
          { field: "sms_response", as: "sms_response", type: "string" },
          { field: "needs_agent", as: "needs_agent", type: "string" },
          { field: "customer_name_guess", as: "customer_name", type: "string" },
        ],
      ),
      n("3", "hitApi", "HubSpot: Lookup customer by phone number", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"phone","operator":"EQ","value":"{sender_phone}"}]}]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.firstname",
              customName: "first_name",
              type: "string",
            },
            {
              name: "results[0].properties.email",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "results[0].properties.plan_tier",
              customName: "plan_tier",
              type: "string",
            },
            { name: "results[0].id", customName: "hubspot_id", type: "string" },
          ],
        },
      }),
      aiNode(
        "4",
        "OpenAI: Generate personalized SMS reply (160 char limit)",
        760,
        "You are an SMS support agent. Compose a personalized response using the customer name and context. Keep it under 160 characters. If the intent is order_status, include a status check link. Return final_sms_reply.",
        "Compose SMS reply for {first_name} ({plan_tier} plan). Intent: {sms_intent}. Original bot response: {sms_response}. Customer message: {sms_body}",
        [{ field: "final_sms_reply", as: "final_sms_reply", type: "string" }],
      ),
      n("5", "condition", "Check: Does this need a human agent?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{needs_agent}", operator: "==", value: "true" },
          ],
        },
      }),
      smsNode(
        "6",
        "Twilio: Send SMS reply to customer",
        1240,
        "{sender_phone}",
        "{final_sms_reply}",
      ),
      slackNode(
        "7",
        "Slack: Escalate complex SMS case to agent queue",
        1480,
        ":iphone: *SMS Escalation Needed*\n*From:* {sender_phone} ({first_name}, {plan_tier})\n*Intent:* {sms_intent}\n*Message:* {sms_body}\n*Bot Reply Sent:* {final_sms_reply}\n\n_Please follow up via SMS or call._",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log SMS support interaction",
        1720,
        "SMS_Support_Log",
        '[["{sender_phone}","{first_name}","{sms_intent}","{needs_agent}","{plan_tier}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
    summary:
      "Receives inbound Twilio SMS, uses AI to classify intent and draft a concise reply, looks up the customer in HubSpot for personalization, then sends a sub-160-character SMS response. If the issue requires human intervention, escalates to Slack with full context. All interactions are logged to Sheets.",
  },

  // ────────────────────────────────────────────────────────────────
  // 107. WhatsApp Support Bot | Support | WhatsApp Business API
  // ────────────────────────────────────────────────────────────────
  whatsapp_support_bot: {
    nodes: [
      trigger(
        "WhatsApp Business API webhook: Message received",
        "webhook_app",
        {
          source: "whatsapp",
          event: "messages",
          howItStarts:
            "Fires when a customer sends a message via WhatsApp Business API, delivering the message text, media type, sender phone number, and message ID.",
          setup:
            "Configure WhatsApp Business API webhook to forward incoming messages. Register the webhook URL in your Meta Business dashboard under WhatsApp settings.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Process WhatsApp text/media and classify intent",
        280,
        "You are a WhatsApp support agent. Analyze the customer message (may include text, image descriptions, or document references). Classify intent (order_inquiry, billing, technical, feedback, complaint). Determine complexity and whether it needs a human. Return intent, complexity, needs_human, extracted_details, response_draft.",
        "WhatsApp message from {sender_phone}: Text: {message_text}. Media type: {media_type}.",
        [
          { field: "intent", as: "wa_intent", type: "string" },
          { field: "complexity", as: "issue_complexity", type: "string" },
          { field: "needs_human", as: "needs_human", type: "string" },
          {
            field: "extracted_details",
            as: "extracted_details",
            type: "string",
          },
          { field: "response_draft", as: "response_draft", type: "string" },
        ],
      ),
      n("3", "hitApi", "HubSpot: Lookup customer profile by phone", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"phone","operator":"EQ","value":"{sender_phone}"}]}]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.firstname",
              customName: "first_name",
              type: "string",
            },
            {
              name: "results[0].properties.email",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "results[0].properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "4",
        "OpenAI: Generate final WhatsApp response",
        760,
        "You are a friendly WhatsApp support agent. Compose a helpful reply personalized with the customer name. Keep it conversational and within WhatsApp formatting conventions (use *bold* for emphasis). If the issue is complex, acknowledge it and mention a ticket will be created. Return final_response.",
        "Compose WhatsApp reply for {first_name}. Intent: {wa_intent}. Complexity: {issue_complexity}. Draft: {response_draft}. Details: {extracted_details}.",
        [{ field: "final_response", as: "wa_final_response", type: "string" }],
      ),
      n(
        "5",
        "condition",
        "Check: AI confidence high enough to auto-reply?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { variable: "{needs_human}", operator: "==", value: "false" },
            ],
          },
        },
      ),
      n("6", "hitApi", "WhatsApp API: Send reply message to customer", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{phone_number_id}/messages",
          headers: [
            { key: "Authorization", value: "Bearer {whatsapp_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "messaging_product", value: "whatsapp" },
            { key: "to", value: "{sender_phone}" },
            { key: "type", value: "text" },
            { key: "text", value: '{"body":"{wa_final_response}"}' },
          ],
          variables: [
            {
              name: "messages[0].id",
              customName: "sent_message_id",
              type: "string",
            },
          ],
        },
      }),
      n("7", "hitApi", "Jira: Create support ticket for complex issues", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"SUP"},"summary":"WhatsApp: {wa_intent} from {first_name}","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"{extracted_details}"}]}]},"issuetype":{"name":"Task"}}',
            },
          ],
          variables: [
            { name: "key", customName: "jira_ticket_key", type: "string" },
          ],
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log WhatsApp support interaction",
        1720,
        "WhatsApp_Support",
        '[["{sender_phone}","{first_name}","{wa_intent}","{issue_complexity}","{needs_human}","{jira_ticket_key}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
    summary:
      "Processes incoming WhatsApp Business API messages, uses AI to classify intent and extract details, looks up the customer in HubSpot, and generates a personalized WhatsApp reply. Simple queries get an instant auto-reply; complex issues create a Jira ticket for agent follow-up. All interactions are logged to Sheets.",
  },

  // ────────────────────────────────────────────────────────────────
  // 108. Live Chat Routing by Urgency | Support | Intercom
  // ────────────────────────────────────────────────────────────────
  live_chat_routing_by_urgency: {
    nodes: [
      trigger(
        "Intercom webhook: New chat conversation started",
        "webhook_app",
        {
          source: "intercom",
          event: "conversation.user.created",
          howItStarts:
            "Fires when a customer initiates a new live chat conversation in Intercom, delivering the conversation ID, user message, user ID, and metadata.",
          setup:
            "Connect Intercom and enable conversation.user.created webhook. Ensure agent availability status is accessible via the Intercom API.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Classify urgency and required skill",
        280,
        "You are a live chat router. Analyze the customer message and classify urgency (critical, high, medium, low). Determine the required agent skill (billing, technical, account, general). Identify if this is a VIP or enterprise customer based on context clues. Return urgency, required_skill, is_vip_signal, topic_summary.",
        "Classify this live chat request for routing: {message_text}",
        [
          { field: "urgency", as: "chat_urgency", type: "string" },
          { field: "required_skill", as: "required_skill", type: "string" },
          { field: "is_vip_signal", as: "is_vip_signal", type: "string" },
          { field: "topic_summary", as: "topic_summary", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Intercom: Check agent availability by skill group",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.intercom.io/admins",
            headers: [
              { key: "Authorization", value: "Bearer {intercom_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "admins",
                customName: "available_agents",
                type: "object",
              },
              { name: "type", customName: "agent_list_type", type: "string" },
            ],
          },
        },
      ),
      n(
        "4",
        "runCode",
        "Select best agent based on skill, load, and urgency",
        760,
        {
          runCodeDetails: {
            code: "const agents = available_agents.filter(a => a.away === false); const skilled = agents.filter(a => (a.custom_attributes?.skills || '').includes(required_skill)); const best_agent = skilled.length > 0 ? skilled[0] : agents[0]; const agents_available = agents.length > 0; return { best_agent_id: best_agent?.id || 'unassigned', best_agent_name: best_agent?.name || 'Queue', agents_available };",
            variables: [
              {
                name: "best_agent_id",
                customName: "best_agent_id",
                type: "string",
              },
              {
                name: "best_agent_name",
                customName: "best_agent_name",
                type: "string",
              },
              {
                name: "agents_available",
                customName: "agents_available",
                type: "boolean",
              },
            ],
          },
        },
      ),
      n("5", "condition", "Check: Are agents currently available?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{agents_available}", operator: "==", value: "true" },
          ],
        },
      }),
      n("6", "hitApi", "Intercom: Route chat to best matched agent", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.intercom.io/conversations/{conversation_id}/parts",
          headers: [
            { key: "Authorization", value: "Bearer {intercom_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "message_type", value: "assignment" },
            { key: "assignee_id", value: "{best_agent_id}" },
            { key: "type", value: "admin" },
          ],
          variables: [
            {
              name: "id",
              customName: "routed_conversation_id",
              type: "string",
            },
          ],
        },
      }),
      n("7", "sendMessage", "Send queue position message to customer", 1480, {
        sendMessageDetails: {
          channel: "intercom",
          conversationId: "{conversation_id}",
          message:
            "Thanks for reaching out! All our agents are currently helping other customers. You're in the queue and we'll be with you shortly. Estimated wait: 5-10 minutes.",
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log chat routing decision",
        1720,
        "Chat_Routing_Log",
        '[["{conversation_id}","{chat_urgency}","{required_skill}","{best_agent_name}","{agents_available}","{is_vip_signal}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
    summary:
      "Routes new Intercom live chat conversations by AI-classified urgency and skill requirement. Checks agent availability, selects the best-matched agent, and either routes the chat directly or sends a queue position message when no agents are available. Logs all routing decisions for staffing optimization.",
  },

  // ────────────────────────────────────────────────────────────────
  // 109. Chat to Ticket Converter | Support | Intercom + Zendesk
  // ────────────────────────────────────────────────────────────────
  chat_to_ticket_converter: {
    nodes: [
      trigger("Intercom webhook: Chat ended unresolved", "webhook_app", {
        source: "intercom",
        event: "conversation.admin.closed",
        howItStarts:
          "Fires when an Intercom conversation is closed without resolution, delivering the full conversation transcript, user info, and conversation metadata.",
        setup:
          "Connect Intercom webhook for conversation.admin.closed events. Filter for conversations tagged as 'unresolved' or where resolution was not confirmed.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Summarize conversation and extract ticket fields",
        280,
        "You are a support analyst. Summarize this unresolved chat conversation into a clear ticket description. Extract: customer name, email, issue category, steps already tried, remaining blockers, and recommended priority. Return customer_name, customer_email, summary, category, steps_tried, remaining_issue, recommended_priority.",
        "Summarize this unresolved chat transcript for ticket creation: {conversation_transcript}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "summary", as: "ticket_summary", type: "string" },
          { field: "category", as: "issue_category", type: "string" },
          { field: "steps_tried", as: "steps_tried", type: "string" },
          { field: "remaining_issue", as: "remaining_issue", type: "string" },
          {
            field: "recommended_priority",
            as: "recommended_priority",
            type: "string",
          },
        ],
      ),
      n("3", "runCode", "Extract structured ticket fields for Zendesk", 520, {
        runCodeDetails: {
          code: "const priority_map = { critical: 'urgent', high: 'high', medium: 'normal', low: 'low' }; const zendesk_priority = priority_map[recommended_priority] || 'normal'; const subject = `[Chat Escalation] ${issue_category}: ${remaining_issue.substring(0, 80)}`; return { zendesk_priority, ticket_subject: subject, full_description: `${ticket_summary}\\n\\nSteps Tried:\\n${steps_tried}\\n\\nRemaining Issue:\\n${remaining_issue}` };",
          variables: [
            {
              name: "zendesk_priority",
              customName: "zendesk_priority",
              type: "string",
            },
            {
              name: "ticket_subject",
              customName: "ticket_subject",
              type: "string",
            },
            {
              name: "full_description",
              customName: "full_description",
              type: "string",
            },
          ],
        },
      }),
      n(
        "4",
        "hitApi",
        "Zendesk: Create support ticket from chat summary",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "ticket",
                value:
                  '{"subject":"{ticket_subject}","description":"{full_description}","priority":"{zendesk_priority}","requester":{"name":"{customer_name}","email":"{customer_email}"},"tags":["chat_escalation","{issue_category}"]}',
              },
            ],
            variables: [
              {
                name: "ticket.id",
                customName: "zendesk_ticket_id",
                type: "string",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Send ticket confirmation to customer",
        1000,
        "{customer_email}",
        "Your support case has been created [#{zendesk_ticket_id}]",
        "Hi {customer_name},\n\nWe're sorry we couldn't fully resolve your issue during chat. We've created a support ticket to continue working on this.\n\nTicket: #{zendesk_ticket_id}\nIssue: {remaining_issue}\n\nA specialist will follow up within 24 hours. You can reply to this email to add more details.\n\nThank you for your patience.",
      ),
      slackNode(
        "6",
        "Slack: Notify support team of chat-to-ticket escalation",
        1240,
        ":arrows_counterclockwise: *Chat Escalated to Ticket #{zendesk_ticket_id}*\n*Customer:* {customer_name} ({customer_email})\n*Category:* {issue_category} | *Priority:* {zendesk_priority}\n*Issue:* {remaining_issue}\n*Steps Tried:* {steps_tried}",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log chat-to-ticket conversion",
        1480,
        "Chat_Escalations",
        '[["{zendesk_ticket_id}","{conversation_id}","{customer_name}","{issue_category}","{zendesk_priority}","{remaining_issue}","{today_date}"]]',
      ),
      n("8", "runCode", "Track chat-to-ticket conversion rate metrics", 1720, {
        runCodeDetails: {
          code: "const conversion_event = { type: 'chat_to_ticket', conversation_id: conversation_id, ticket_id: zendesk_ticket_id, category: issue_category, priority: zendesk_priority, timestamp: new Date().toISOString() }; return { conversion_logged: true, event_type: conversion_event.type };",
          variables: [
            {
              name: "conversion_logged",
              customName: "conversion_logged",
              type: "boolean",
            },
            { name: "event_type", customName: "event_type", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When an Intercom chat closes unresolved, AI summarizes the transcript and extracts structured ticket fields. A Zendesk ticket is created with the chat context, the customer receives an email confirmation, the support team gets a Slack alert, and the escalation is logged to Sheets with conversion tracking metrics.",
  },

  // ────────────────────────────────────────────────────────────────
  // 110. Case Status Inquiry Bot | Support
  // ────────────────────────────────────────────────────────────────
  case_status_inquiry_bot: {
    nodes: [
      trigger("Webhook: Customer asks about ticket status", "webhook_app", {
        source: "intercom",
        event: "conversation.user.created",
        howItStarts:
          "Fires when a customer sends a message asking about an existing ticket or case status, delivering the message text, conversation ID, and user info.",
        setup:
          "Connect Intercom webhook for new conversations. This workflow detects status inquiry intent and fetches the real-time ticket status from Zendesk.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract ticket reference number from message",
        280,
        "You are a support bot. The customer is asking about a ticket or case status. Extract any ticket reference number, case ID, or order number from their message. If no explicit number, extract enough details to search. Return ticket_ref, has_reference, search_fallback_terms.",
        "Extract ticket reference from customer message: {message_text}",
        [
          { field: "ticket_ref", as: "ticket_ref", type: "string" },
          { field: "has_reference", as: "has_reference", type: "string" },
          {
            field: "search_fallback_terms",
            as: "search_terms",
            type: "string",
          },
        ],
      ),
      n("3", "hitApi", "Zendesk: Fetch ticket details by ID", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{ticket_ref}.json",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "ticket.status",
              customName: "ticket_status",
              type: "string",
            },
            {
              name: "ticket.subject",
              customName: "ticket_subject",
              type: "string",
            },
            {
              name: "ticket.updated_at",
              customName: "last_updated",
              type: "string",
            },
            {
              name: "ticket.assignee_id",
              customName: "assignee_id",
              type: "string",
            },
            {
              name: "ticket.priority",
              customName: "ticket_priority",
              type: "string",
            },
          ],
        },
      }),
      n("4", "runCode", "Format human-readable status and ETA", 760, {
        runCodeDetails: {
          code: "const status_map = { new: 'Received and in queue', open: 'Being actively worked on', pending: 'Waiting for additional information from you', hold: 'On hold — our team is investigating', solved: 'Resolved', closed: 'Closed' }; const friendly_status = status_map[ticket_status] || 'In progress'; const last_update = new Date(last_updated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); const ticket_found = !!ticket_status; return { friendly_status, last_update, ticket_found };",
          variables: [
            {
              name: "friendly_status",
              customName: "friendly_status",
              type: "string",
            },
            { name: "last_update", customName: "last_update", type: "string" },
            {
              name: "ticket_found",
              customName: "ticket_found",
              type: "boolean",
            },
          ],
        },
      }),
      n("5", "condition", "Check: Was the ticket found?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{ticket_found}", operator: "==", value: "true" },
          ],
        },
      }),
      n("6", "sendMessage", "Send ticket status reply to customer", 1240, {
        sendMessageDetails: {
          channel: "intercom",
          conversationId: "{conversation_id}",
          message:
            "Here's the status of ticket #{ticket_ref}:\n\nSubject: {ticket_subject}\nStatus: {friendly_status}\nPriority: {ticket_priority}\nLast Updated: {last_update}\n\nIf you have additional details to share, reply here and we'll update your ticket.",
        },
      }),
      slackNode(
        "7",
        "Slack: Log status inquiry for trending analysis",
        1480,
        ":mag: *Status Inquiry*\n*Ticket:* #{ticket_ref} | *Status:* {ticket_status}\n*Customer asked:* {message_text}\n*Found:* {ticket_found}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log status inquiry",
        1720,
        "Status_Inquiries",
        '[["{ticket_ref}","{ticket_status}","{ticket_found}","{has_reference}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Detects when a customer asks about their ticket status, extracts the ticket reference number with AI, fetches real-time status from Zendesk, formats a human-friendly status message, and replies in the chat. If the ticket is not found, routes to Slack for agent assistance. Logs all inquiries to Sheets for trend analysis.",
  },

  // ────────────────────────────────────────────────────────────────
  // 111. Waiting-on-Customer Reminder Chain | Support | Zendesk
  // ────────────────────────────────────────────────────────────────
  waiting_on_customer_reminder_chain: {
    nodes: [
      trigger(
        "Zendesk webhook: Ticket status changed to pending",
        "webhook_app",
        {
          source: "zendesk",
          event: "ticket.status_changed",
          howItStarts:
            "Fires when a Zendesk ticket moves to pending (waiting on customer) status, delivering the ticket ID, requester email, subject, and last agent comment.",
          setup:
            "Connect Zendesk webhook for ticket status changes. Filter for transitions to 'pending' status only.",
          inputFields: [],
        },
      ),
      n("2", "delayAction", "Wait 48 hours for customer response", 280, {
        delayDetails: { duration: 48, unit: "hours" },
      }),
      n("3", "hitApi", "Zendesk: Check if ticket is still pending", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{ticket_id}.json",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "ticket.status",
              customName: "current_status",
              type: "string",
            },
            {
              name: "ticket.requester.email",
              customName: "requester_email",
              type: "string",
            },
            {
              name: "ticket.requester.name",
              customName: "requester_name",
              type: "string",
            },
            {
              name: "ticket.subject",
              customName: "ticket_subject",
              type: "string",
            },
          ],
        },
      }),
      n("4", "condition", "Check: Is ticket still pending after 48h?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{current_status}", operator: "==", value: "pending" },
          ],
        },
      }),
      emailNode(
        "5",
        "Send friendly reminder to customer",
        1000,
        "{requester_email}",
        "We're still waiting on your input — Ticket #{ticket_id}",
        "Hi {requester_name},\n\nWe wanted to follow up on your support ticket #{ticket_id}: {ticket_subject}.\n\nWe need some additional information from you to continue resolving your issue. Could you please reply with the details we requested?\n\nIf your issue has been resolved, just let us know and we'll close the ticket.\n\nThank you,\nSupport Team",
      ),
      n("6", "delayAction", "Wait 72 more hours for final response", 1240, {
        delayDetails: { duration: 72, unit: "hours" },
      }),
      n("7", "runCode", "Auto-close ticket if still no response", 1480, {
        runCodeDetails: {
          code: "const total_wait_hours = 120; const auto_close_reason = 'No customer response after 120 hours (48h + 72h reminder). Auto-closing per SLA policy.'; return { total_wait_hours, auto_close_reason, should_close: true };",
          variables: [
            {
              name: "total_wait_hours",
              customName: "total_wait_hours",
              type: "number",
            },
            {
              name: "auto_close_reason",
              customName: "auto_close_reason",
              type: "string",
            },
            {
              name: "should_close",
              customName: "should_close",
              type: "boolean",
            },
          ],
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log pending ticket resolution",
        1720,
        "Pending_Tickets",
        '[["{ticket_id}","{requester_email}","{ticket_subject}","{current_status}","{total_wait_hours}","{auto_close_reason}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggers when a Zendesk ticket enters pending status. Waits 48 hours, then checks if the customer responded. If still pending, sends a friendly email reminder and waits another 72 hours. If still no response, prepares auto-close with a documented reason and logs the outcome to Sheets for SLA tracking.",
  },

  // ────────────────────────────────────────────────────────────────
  // 112. Ticket SLA Breach Warning | Support | Zendesk
  // ────────────────────────────────────────────────────────────────
  ticket_sla_breach_warning: {
    nodes: [
      trigger(
        "Schedule: Check for SLA-approaching tickets every 15 minutes",
        "schedule",
        {
          schedule:
            "Every 15 minutes — scans Zendesk for tickets approaching SLA breach",
          howItStarts:
            "Runs every 15 minutes to query Zendesk for open tickets whose SLA deadline is within 1 hour, enabling proactive escalation before a breach occurs.",
          setup:
            "Configure a 15-minute schedule. Ensure Zendesk SLA policies are set with custom field storing the SLA deadline timestamp.",
          inputFields: [],
        },
      ),
      n("2", "hitApi", "Zendesk: Fetch tickets approaching SLA deadline", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/search.json?query=type:ticket status<solved custom_field_360001<{one_hour_from_now}",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "results", customName: "at_risk_tickets", type: "object" },
            { name: "count", customName: "at_risk_count", type: "number" },
          ],
        },
      }),
      n(
        "3",
        "runCode",
        "Compute time remaining and breach severity for each ticket",
        760,
        {
          runCodeDetails: {
            code: "const now = Date.now(); const tickets = at_risk_tickets.map(t => { const deadline = new Date(t.custom_fields?.find(f => f.id === 360001)?.value).getTime(); const minutes_remaining = Math.round((deadline - now) / 60000); const severity = minutes_remaining <= 15 ? 'critical' : minutes_remaining <= 30 ? 'high' : 'warning'; return { id: t.id, subject: t.subject, assignee: t.assignee_id, priority: t.priority, minutes_remaining, severity }; }); const critical_count = tickets.filter(t => t.severity === 'critical').length; return { processed_tickets: JSON.stringify(tickets), critical_count, total_at_risk: tickets.length };",
            variables: [
              {
                name: "processed_tickets",
                customName: "processed_tickets",
                type: "string",
              },
              {
                name: "critical_count",
                customName: "critical_count",
                type: "number",
              },
              {
                name: "total_at_risk",
                customName: "total_at_risk",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: Any tickets within 1 hour of SLA breach?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { variable: "{total_at_risk}", operator: ">", value: "0" },
            ],
          },
        },
      ),
      slackNode(
        "5",
        "Slack: Send urgent SLA breach warning to #support-alerts",
        1240,
        ":warning: *SLA Breach Warning — {total_at_risk} tickets at risk*\n*Critical (< 15 min):* {critical_count}\n\n{processed_tickets}\n\n_Action required: respond to these tickets immediately to avoid SLA breach._",
      ),
      emailNode(
        "6",
        "Email: Alert agent and manager about imminent SLA breach",
        1480,
        "{agent_email},{manager_email}",
        "URGENT: {total_at_risk} tickets approaching SLA breach",
        "This is an automated SLA warning.\n\n{total_at_risk} tickets are within 1 hour of breaching their SLA deadlines. {critical_count} are critical (under 15 minutes remaining).\n\nPlease take immediate action to respond to or reassign these tickets.\n\nTicket Details:\n{processed_tickets}",
      ),
      n("7", "hitApi", "Zendesk: Escalate priority on critical tickets", 1720, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/update_many.json?ids={critical_ticket_ids}",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "ticket",
              value:
                '{"priority":"urgent","tags":["sla_at_risk"],"custom_fields":[{"id":360002,"value":"sla_warning_sent"}]}',
            },
          ],
          variables: [
            {
              name: "job_status.id",
              customName: "escalation_job_id",
              type: "string",
            },
          ],
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log SLA breach warnings",
        1720,
        "SLA_Warnings",
        '[["{total_at_risk}","{critical_count}","{today_date}","{timestamp}"]]',
        480,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Runs every 15 minutes to scan Zendesk for tickets approaching SLA breach. Computes time remaining and severity, alerts the Slack support channel with an urgent warning, emails agents and managers, escalates critical tickets' priority in Zendesk, and logs all warnings to Sheets for breach pattern analysis.",
  },

  // ────────────────────────────────────────────────────────────────
  // 113. Ticket Aging Alert | Support | Zendesk
  // ────────────────────────────────────────────────────────────────
  ticket_aging_alert: {
    nodes: [
      trigger("Schedule: Daily aged ticket scan at 8 AM", "schedule", {
        schedule:
          "Daily at 8:00 AM — scans Zendesk for tickets older than 48 hours without update",
        howItStarts:
          "Runs daily to identify Zendesk tickets that have not been updated in over 48 hours, separating customer-stalled from internally-stalled tickets for targeted action.",
        setup:
          "Configure a daily schedule at 8:00 AM. Ensure Zendesk tickets have updated_at timestamps accessible via API.",
        inputFields: [],
      }),
      n("2", "hitApi", "Zendesk: Fetch tickets not updated in 48+ hours", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/search.json?query=type:ticket status<solved updated<{48_hours_ago}",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "results", customName: "aged_tickets", type: "object" },
            { name: "count", customName: "aged_count", type: "number" },
          ],
        },
      }),
      n(
        "3",
        "runCode",
        "Separate customer-stalled vs internally-stalled tickets",
        520,
        {
          runCodeDetails: {
            code: "const customer_stalled = aged_tickets.filter(t => t.status === 'pending'); const internal_stalled = aged_tickets.filter(t => t.status === 'open' || t.status === 'new'); const hours_since_update = (t) => Math.round((Date.now() - new Date(t.updated_at).getTime()) / 3600000); const internal_summary = internal_stalled.map(t => `#${t.id}: ${t.subject} (${hours_since_update(t)}h stalled)`).join('\\n'); const customer_summary = customer_stalled.map(t => `#${t.id}: ${t.subject} (${hours_since_update(t)}h waiting)`).join('\\n'); return { internal_stalled_count: internal_stalled.length, customer_stalled_count: customer_stalled.length, internal_summary, customer_summary };",
            variables: [
              {
                name: "internal_stalled_count",
                customName: "internal_stalled_count",
                type: "number",
              },
              {
                name: "customer_stalled_count",
                customName: "customer_stalled_count",
                type: "number",
              },
              {
                name: "internal_summary",
                customName: "internal_summary",
                type: "string",
              },
              {
                name: "customer_summary",
                customName: "customer_summary",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check: Any tickets internally stalled > 48h?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{internal_stalled_count}", operator: ">", value: "0" },
          ],
        },
      }),
      slackNode(
        "5",
        "Slack: Alert agents about their stalled tickets",
        1000,
        ":hourglass_flowing_sand: *Daily Aging Report — {aged_count} stalled tickets*\n\n*Internally Stalled ({internal_stalled_count}):*\n{internal_summary}\n\n*Waiting on Customer ({customer_stalled_count}):*\n{customer_summary}\n\n_Please update or close stalled tickets today._",
      ),
      emailNode(
        "6",
        "Email: Send aging report to support manager",
        1240,
        "{support_manager_email}",
        "Support Ticket Aging Report — {aged_count} tickets stalled",
        "Daily Aging Report:\n\nInternally Stalled ({internal_stalled_count} tickets):\n{internal_summary}\n\nWaiting on Customer ({customer_stalled_count} tickets):\n{customer_summary}\n\nPlease review and take action on internally stalled tickets.",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log daily aging trend data",
        1480,
        "Aging_Trends",
        '[["{aged_count}","{internal_stalled_count}","{customer_stalled_count}","{today_date}"]]',
      ),
      n("8", "runCode", "Compute aging velocity metrics", 1720, {
        runCodeDetails: {
          code: "const avg_age_hours = aged_tickets.reduce((sum, t) => sum + Math.round((Date.now() - new Date(t.updated_at).getTime()) / 3600000), 0) / Math.max(aged_tickets.length, 1); const pct_internal = aged_tickets.length > 0 ? Math.round(internal_stalled_count / aged_tickets.length * 100) : 0; return { avg_age_hours: Math.round(avg_age_hours), pct_internally_stalled: pct_internal };",
          variables: [
            {
              name: "avg_age_hours",
              customName: "avg_age_hours",
              type: "number",
            },
            {
              name: "pct_internally_stalled",
              customName: "pct_internally_stalled",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Runs daily at 8 AM to find Zendesk tickets stalled for over 48 hours. Separates internally-stalled from customer-stalled, alerts agents via Slack, emails the manager with a detailed aging report, logs daily trends to Sheets, and computes aging velocity metrics to track improvement over time.",
  },

  // ────────────────────────────────────────────────────────────────
  // 114. Duplicate Ticket Detector | Support | Zendesk
  // ────────────────────────────────────────────────────────────────
  duplicate_ticket_detector: {
    nodes: [
      trigger("Zendesk webhook: New ticket created", "webhook_app", {
        source: "zendesk",
        event: "ticket.created",
        howItStarts:
          "Fires when a new Zendesk ticket is created, sending the ticket ID, subject, description, and requester email for duplicate detection against recent tickets.",
        setup:
          "Connect Zendesk webhook for ticket.created events. This workflow compares new tickets against the last 7 days of open tickets to find potential duplicates.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract issue fingerprint from new ticket",
        280,
        "You are a support ticket deduplication specialist. Extract a normalized issue fingerprint from this ticket: the core problem statement, affected feature/product, error codes or symptoms, and requester context. Return issue_fingerprint, core_problem, product_area, error_indicators, search_terms.",
        "Extract issue fingerprint from new ticket. Subject: {ticket_subject}. Description: {ticket_description}. Requester: {requester_email}.",
        [
          {
            field: "issue_fingerprint",
            as: "issue_fingerprint",
            type: "string",
          },
          { field: "core_problem", as: "core_problem", type: "string" },
          { field: "product_area", as: "product_area", type: "string" },
          { field: "error_indicators", as: "error_indicators", type: "string" },
          { field: "search_terms", as: "search_terms", type: "string" },
        ],
      ),
      n("3", "hitApi", "Zendesk: Search for similar recent tickets", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/search.json?query=type:ticket status<solved created>7daysAgo {search_terms}",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "results", customName: "similar_tickets", type: "object" },
            { name: "count", customName: "similar_count", type: "number" },
          ],
        },
      }),
      n(
        "4",
        "runCode",
        "Compute similarity scores against found tickets",
        760,
        {
          runCodeDetails: {
            code: "const scored = similar_tickets.filter(t => t.id !== ticket_id).map(t => { const subject_overlap = t.subject.toLowerCase().split(' ').filter(w => search_terms.toLowerCase().includes(w)).length; const same_requester = t.requester_id === requester_id; const score = Math.min(100, subject_overlap * 20 + (same_requester ? 30 : 0)); return { id: t.id, subject: t.subject, score, same_requester }; }).sort((a, b) => b.score - a.score); const best_match = scored[0]; const match_found = best_match && best_match.score > 90; return { best_match_id: best_match?.id || 'none', best_match_subject: best_match?.subject || 'N/A', best_match_score: best_match?.score || 0, match_found };",
            variables: [
              {
                name: "best_match_id",
                customName: "best_match_id",
                type: "string",
              },
              {
                name: "best_match_subject",
                customName: "best_match_subject",
                type: "string",
              },
              {
                name: "best_match_score",
                customName: "best_match_score",
                type: "number",
              },
              {
                name: "match_found",
                customName: "match_found",
                type: "boolean",
              },
            ],
          },
        },
      ),
      n("5", "condition", "Check: Duplicate match confidence > 90%?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{best_match_score}", operator: ">", value: "90" },
          ],
        },
      }),
      n("6", "hitApi", "Zendesk: Suggest merge via internal note", 1240, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{ticket_id}.json",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "ticket",
              value:
                '{"comment":{"body":"**Potential Duplicate Detected (score: {best_match_score}%)**\\n\\nThis ticket may be a duplicate of #{best_match_id}: {best_match_subject}\\n\\nPlease review and merge if confirmed.","public":false},"tags":["potential_duplicate"]}',
            },
          ],
          variables: [
            {
              name: "ticket.id",
              customName: "noted_ticket_id",
              type: "string",
            },
          ],
        },
      }),
      slackNode(
        "7",
        "Slack: Alert agent of potential duplicate",
        1480,
        ":two: *Potential Duplicate Detected*\n*New Ticket:* #{ticket_id} — {ticket_subject}\n*Matches:* #{best_match_id} — {best_match_subject}\n*Confidence:* {best_match_score}%\n*Product Area:* {product_area}\n\n_Please review and merge if appropriate._",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log duplicate detection results",
        1720,
        "Duplicate_Detection",
        '[["{ticket_id}","{best_match_id}","{best_match_score}","{product_area}","{match_found}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "On each new Zendesk ticket, AI extracts an issue fingerprint and searches for similar recent tickets. A similarity score is computed; if above 90%, a Zendesk internal note suggests merging and an agent receives a Slack alert. All detection results are logged to Sheets for duplicate rate monitoring.",
  },

  // ────────────────────────────────────────────────────────────────
  // 115. Article Recommendation on Ticket Creation | Support | Zendesk + Pinecone
  // ────────────────────────────────────────────────────────────────
  article_recommendation_on_ticket: {
    nodes: [
      trigger("Zendesk webhook: New ticket created", "webhook_app", {
        source: "zendesk",
        event: "ticket.created",
        howItStarts:
          "Fires when a new Zendesk ticket is created, sending the ticket ID, subject, description, and requester email for proactive article recommendation.",
        setup:
          "Connect Zendesk webhook for ticket.created. Ensure your knowledge base articles are indexed in Pinecone for vector search.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Analyze problem and extract search query",
        280,
        "You are a support knowledge specialist. Analyze the ticket to understand the core problem. Generate an optimized search query for finding relevant help center articles. Return problem_summary, search_query, product_area, complexity_level.",
        "Analyze this support ticket for article matching. Subject: {ticket_subject}. Description: {ticket_description}.",
        [
          { field: "problem_summary", as: "problem_summary", type: "string" },
          { field: "search_query", as: "kb_search_query", type: "string" },
          { field: "product_area", as: "product_area", type: "string" },
          { field: "complexity_level", as: "complexity_level", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Pinecone: Search knowledge base for relevant articles",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{pinecone_index}-{pinecone_project}.svc.{pinecone_env}.pinecone.io/query",
            headers: [
              { key: "Api-Key", value: "{pinecone_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "vector", value: "{search_embedding}" },
              { key: "topK", value: "5" },
              { key: "includeMetadata", value: "true" },
              { key: "namespace", value: "help_center" },
            ],
            variables: [
              {
                name: "matches[0].metadata.title",
                customName: "article_1_title",
                type: "string",
              },
              {
                name: "matches[0].metadata.url",
                customName: "article_1_url",
                type: "string",
              },
              {
                name: "matches[0].score",
                customName: "article_1_score",
                type: "number",
              },
              {
                name: "matches[1].metadata.title",
                customName: "article_2_title",
                type: "string",
              },
              {
                name: "matches[1].metadata.url",
                customName: "article_2_url",
                type: "string",
              },
              {
                name: "matches[2].metadata.title",
                customName: "article_3_title",
                type: "string",
              },
              {
                name: "matches[2].metadata.url",
                customName: "article_3_url",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "runCode", "Rank articles and check relevance threshold", 760, {
        runCodeDetails: {
          code: "const high_relevance = article_1_score > 0.8; const articles = [{ title: article_1_title, url: article_1_url, score: article_1_score }, { title: article_2_title, url: article_2_url }, { title: article_3_title, url: article_3_url }].filter(a => a.title); const article_list = articles.map((a, i) => `${i + 1}. ${a.title}: ${a.url}`).join('\\n'); return { high_relevance, article_count: articles.length, article_list };",
          variables: [
            {
              name: "high_relevance",
              customName: "high_relevance",
              type: "boolean",
            },
            {
              name: "article_count",
              customName: "article_count",
              type: "number",
            },
            {
              name: "article_list",
              customName: "article_list",
              type: "string",
            },
          ],
        },
      }),
      n("5", "condition", "Check: Top article relevance score > 80%?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{high_relevance}", operator: "==", value: "true" },
          ],
        },
      }),
      emailNode(
        "6",
        "Email: Send relevant articles to customer proactively",
        1240,
        "{requester_email}",
        "Helpful resources for your request: {ticket_subject}",
        "Hi {requester_name},\n\nWhile our team reviews your request (ticket #{ticket_id}), here are some articles that may help:\n\n{article_list}\n\nIf any of these resolve your issue, just reply to let us know. Otherwise, an agent will follow up shortly.\n\nBest,\nSupport Team",
      ),
      n(
        "7",
        "hitApi",
        "Zendesk: Add article recommendations as internal note",
        1480,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{ticket_id}.json",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "ticket",
                value:
                  '{"comment":{"body":"**AI Article Recommendations:**\\n{article_list}\\n\\nTop match score: {article_1_score}. Articles sent to customer: {high_relevance}.","public":false}}',
              },
            ],
            variables: [
              {
                name: "ticket.id",
                customName: "updated_ticket_id",
                type: "string",
              },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Track article recommendation effectiveness",
        1720,
        "Article_Recommendations",
        '[["{ticket_id}","{product_area}","{article_1_title}","{article_1_score}","{high_relevance}","{article_count}","{today_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When a new Zendesk ticket is created, AI analyzes the problem and searches Pinecone for relevant help center articles. If the top match exceeds 80% relevance, articles are emailed to the customer proactively. Article recommendations are always added as a Zendesk internal note for agents, and effectiveness is tracked in Sheets.",
  },

  // ────────────────────────────────────────────────────────────────
  // 116. Resolved Ticket Follow-Up Check | Support | Zendesk
  // ────────────────────────────────────────────────────────────────
  resolved_ticket_followup_check: {
    nodes: [
      trigger("Zendesk webhook: Ticket marked as resolved", "webhook_app", {
        source: "zendesk",
        event: "ticket.solved",
        howItStarts:
          "Fires when a Zendesk ticket is marked as solved/resolved, delivering the ticket ID, subject, requester email, and resolution details.",
        setup:
          "Connect Zendesk webhook for ticket.solved events. This workflow follows up with the customer to confirm satisfaction after resolution.",
        inputFields: [],
      }),
      n("2", "delayAction", "Wait 24 hours after resolution", 280, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      emailNode(
        "3",
        "Email: Send satisfaction check to customer",
        520,
        "{requester_email}",
        "Is your issue resolved? Re: {ticket_subject} [#{ticket_id}]",
        "Hi {requester_name},\n\nYour support ticket #{ticket_id} ({ticket_subject}) was recently resolved. We want to make sure everything is working as expected.\n\nIs your issue fully resolved?\n- Reply YES if everything is good\n- Reply NO if you need further assistance\n\nIf we don't hear back within 48 hours, we'll close the ticket.\n\nThank you,\nSupport Team",
      ),
      n("4", "delayAction", "Wait 48 hours for customer response", 760, {
        delayDetails: { duration: 48, unit: "hours" },
      }),
      n("5", "condition", "Check: Did the customer reply?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{customer_replied}", operator: "==", value: "true" },
          ],
        },
      }),
      n("6", "runCode", "Process response — reopen or confirm closure", 1240, {
        runCodeDetails: {
          code: "const reply_lower = (customer_reply || '').toLowerCase(); const needs_reopen = reply_lower.includes('no') || reply_lower.includes('not resolved') || reply_lower.includes('still'); const action = needs_reopen ? 'reopen' : 'close'; const status_update = needs_reopen ? 'open' : 'closed'; return { action, status_update, needs_reopen };",
          variables: [
            { name: "action", customName: "follow_up_action", type: "string" },
            {
              name: "status_update",
              customName: "status_update",
              type: "string",
            },
            {
              name: "needs_reopen",
              customName: "needs_reopen",
              type: "boolean",
            },
          ],
        },
      }),
      sheetsNode(
        "7",
        "Google Sheets: Log follow-up outcome",
        1480,
        "Resolution_Followups",
        '[["{ticket_id}","{requester_email}","{ticket_subject}","{customer_replied}","{follow_up_action}","{status_update}","{today_date}"]]',
      ),
      n(
        "8",
        "hitApi",
        "Zendesk: Update ticket status based on response",
        1720,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{ticket_id}.json",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "ticket",
                value:
                  '{"status":"{status_update}","comment":{"body":"Follow-up result: {follow_up_action}. Customer replied: {customer_replied}.","public":false}}',
              },
            ],
            variables: [
              {
                name: "ticket.id",
                customName: "final_ticket_id",
                type: "string",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "After a Zendesk ticket is resolved, waits 24 hours then emails the customer a satisfaction check. Waits 48 more hours for a reply; if the customer confirms resolution or doesn't reply, closes the ticket. If the customer says the issue persists, reopens the ticket. Logs all follow-up outcomes to Sheets and updates Zendesk.",
  },

  // ────────────────────────────────────────────────────────────────
  // 117. CSAT After Resolution | Support | Zendesk
  // ────────────────────────────────────────────────────────────────
  csat_after_resolution: {
    nodes: [
      trigger("Zendesk webhook: Ticket resolved", "webhook_app", {
        source: "zendesk",
        event: "ticket.solved",
        howItStarts:
          "Fires when a Zendesk ticket is resolved, delivering the ticket ID, requester email, subject, assigned agent, and resolution time for CSAT survey dispatch.",
        setup:
          "Connect Zendesk webhook for ticket.solved. This workflow checks survey fatigue before sending CSAT to avoid over-surveying customers.",
        inputFields: [],
      }),
      n("2", "runCode", "Check survey fatigue — 30-day cooldown window", 280, {
        runCodeDetails: {
          code: "const last_survey_date = last_csat_sent_date ? new Date(last_csat_sent_date) : null; const days_since = last_survey_date ? Math.round((Date.now() - last_survey_date.getTime()) / 86400000) : 999; const eligible = days_since >= 30; return { survey_eligible: eligible, days_since_last_survey: days_since };",
          variables: [
            {
              name: "survey_eligible",
              customName: "survey_eligible",
              type: "boolean",
            },
            {
              name: "days_since_last_survey",
              customName: "days_since_last",
              type: "number",
            },
          ],
        },
      }),
      n("3", "condition", "Check: Customer eligible for CSAT survey?", 520, {
        conditionDetails: {
          conditions: [
            { variable: "{survey_eligible}", operator: "==", value: "true" },
          ],
        },
      }),
      n("4", "delayAction", "Wait 2 hours after resolution for survey", 760, {
        delayDetails: { duration: 2, unit: "hours" },
      }),
      emailNode(
        "5",
        "Email: Send CSAT satisfaction survey",
        1000,
        "{requester_email}",
        "How did we do? Rate your support experience [#{ticket_id}]",
        "Hi {requester_name},\n\nYour recent support request (#{ticket_id}: {ticket_subject}) has been resolved.\n\nHow would you rate your experience?\n\n5 - Excellent\n4 - Good\n3 - Average\n2 - Below Average\n1 - Poor\n\nClick to rate: {survey_link}\n\nYour feedback helps us improve. It takes less than 30 seconds.\n\nThank you,\nSupport Team",
      ),
      aiNode(
        "6",
        "OpenAI: Analyze CSAT response and detect themes",
        1240,
        "You are a CSAT analyst. Analyze the customer's survey response including their rating and any free-text comments. Extract themes (speed, knowledge, empathy, resolution quality). Flag any concerning patterns. Return csat_score, themes, sentiment, needs_attention.",
        "Analyze CSAT response for ticket #{ticket_id}. Rating: {csat_rating}. Comments: {csat_comments}. Agent: {agent_name}.",
        [
          { field: "csat_score", as: "csat_score", type: "number" },
          { field: "themes", as: "csat_themes", type: "string" },
          { field: "sentiment", as: "response_sentiment", type: "string" },
          { field: "needs_attention", as: "needs_attention", type: "string" },
        ],
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log CSAT score and themes",
        1480,
        "CSAT_Scores",
        '[["{ticket_id}","{requester_email}","{agent_name}","{csat_score}","{csat_themes}","{response_sentiment}","{today_date}"]]',
      ),
      slackNode(
        "8",
        "Slack: Alert if low CSAT score received",
        1720,
        ":chart_with_downwards_trend: *Low CSAT Alert*\n*Ticket:* #{ticket_id} | *Score:* {csat_score}/5\n*Agent:* {agent_name}\n*Themes:* {csat_themes}\n*Comments:* {csat_comments}\n*Sentiment:* {response_sentiment}\n\n_Review this interaction for coaching opportunity._",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "8", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "After a Zendesk ticket is resolved, checks a 30-day survey fatigue window to avoid over-surveying. If eligible, waits 2 hours then sends a CSAT email survey. When the response comes in, AI analyzes the rating, themes, and sentiment, logs the score to Sheets, and sends a Slack alert if the score is low for coaching review.",
  },

  // ────────────────────────────────────────────────────────────────
  // 118. CES After Onboarding Step | Support | HubSpot
  // ────────────────────────────────────────────────────────────────
  ces_after_onboarding_step: {
    nodes: [
      trigger(
        "Webhook: Customer completed onboarding milestone",
        "webhook_app",
        {
          source: "hubspot",
          event: "contact.propertyChange",
          howItStarts:
            "Fires when a customer completes an onboarding milestone (e.g., first integration, first campaign sent), sending the milestone type, customer ID, and completion timestamp.",
          setup:
            "Connect HubSpot webhook for contact property changes. Filter for onboarding milestone fields (onboarding_step_completed) updates.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Lookup customer onboarding journey details",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}?properties=email,firstname,company,onboarding_step,onboarding_start_date,plan_tier",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
            ],
            body: [],
            variables: [
              {
                name: "properties.email",
                customName: "customer_email",
                type: "string",
              },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.company",
                customName: "company_name",
                type: "string",
              },
              {
                name: "properties.onboarding_step",
                customName: "milestone_name",
                type: "string",
              },
              {
                name: "properties.onboarding_start_date",
                customName: "onboarding_start",
                type: "string",
              },
              {
                name: "properties.plan_tier",
                customName: "plan_tier",
                type: "string",
              },
            ],
          },
        },
      ),
      emailNode(
        "3",
        "Email: Send CES (Customer Effort Score) survey",
        520,
        "{customer_email}",
        "Quick question about your {milestone_name} setup",
        "Hi {first_name},\n\nCongratulations on completing {milestone_name}! We'd love your quick feedback.\n\nHow easy was it to complete this step?\n\n1 - Very Difficult\n2 - Difficult\n3 - Neutral\n4 - Easy\n5 - Very Easy\n\nRate here: {ces_survey_link}\n\nYour feedback helps us make the onboarding experience smoother for everyone.\n\nThank you,\nCustomer Success Team",
      ),
      n("4", "delayAction", "Wait 48 hours for survey response", 760, {
        delayDetails: { duration: 48, unit: "hours" },
      }),
      n("5", "runCode", "Collect and process CES response", 1000, {
        runCodeDetails: {
          code: "const ces_score = parseInt(ces_response_value) || 0; const high_effort = ces_score <= 2; const days_in_onboarding = Math.round((Date.now() - new Date(onboarding_start).getTime()) / 86400000); return { ces_score, high_effort, days_in_onboarding, effort_level: high_effort ? 'high' : ces_score <= 3 ? 'medium' : 'low' };",
          variables: [
            { name: "ces_score", customName: "ces_score", type: "number" },
            { name: "high_effort", customName: "high_effort", type: "boolean" },
            {
              name: "days_in_onboarding",
              customName: "days_in_onboarding",
              type: "number",
            },
            {
              name: "effort_level",
              customName: "effort_level",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "6",
        "OpenAI: Analyze effort drivers and recommend improvements",
        1240,
        "You are a customer success analyst. Analyze this CES survey result in context of the onboarding milestone. Identify likely effort drivers based on the milestone type and score. Recommend specific improvements. Return effort_analysis, recommended_improvements, churn_risk_signal.",
        "Analyze CES for {first_name} at {company_name}. Milestone: {milestone_name}. CES Score: {ces_score}/5. Days in onboarding: {days_in_onboarding}. Plan: {plan_tier}.",
        [
          { field: "effort_analysis", as: "effort_analysis", type: "string" },
          {
            field: "recommended_improvements",
            as: "recommended_improvements",
            type: "string",
          },
          {
            field: "churn_risk_signal",
            as: "churn_risk_signal",
            type: "string",
          },
        ],
      ),
      n("7", "condition", "Check: High effort score (CES <= 2)?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "{high_effort}", operator: "==", value: "true" },
          ],
        },
      }),
      slackNode(
        "8",
        "Slack: Route high-effort flag to Customer Success",
        1720,
        ":warning: *High-Effort Onboarding Alert*\n*Customer:* {first_name} ({company_name}, {plan_tier})\n*Milestone:* {milestone_name} | *CES:* {ces_score}/5\n*Days in Onboarding:* {days_in_onboarding}\n*Effort Analysis:* {effort_analysis}\n*Churn Risk:* {churn_risk_signal}\n*Recommended Action:* {recommended_improvements}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
    summary:
      "After a customer completes an onboarding milestone, sends a CES survey to measure effort. Waits 48 hours for the response, then AI analyzes effort drivers and churn risk signals. If the effort score is high (difficult experience), routes an alert to Customer Success via Slack with improvement recommendations.",
  },
};
